import { Link } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';

const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61579672283759';

interface FooterColumn {
  title: string;
  links: { label: string; href: string }[];
}

interface FooterProps {
  variant?: 'service' | 'vendor';
}

const serviceColumns: FooterColumn[] = [
  {
    title: 'Sekce portálu',
    links: [
      { label: 'Katalog stánkařů', href: '/katalog' },
      { label: 'Aktivity a řemesla', href: '/aktivity' },
      { label: 'Služby a personál', href: '#' },
      { label: 'Technické zajištění', href: '#' },
    ],
  },
  {
    title: 'Pro partnery',
    links: [
      { label: 'Registrace služby', href: '/pridat' },
      { label: 'Výhody členství', href: '#' },
      { label: 'Ceník', href: '#' },
    ],
  },
];

const vendorColumns: FooterColumn[] = [
  {
    title: 'Pro pořadatele',
    links: [
      { label: 'Katalog stánkařů', href: '/katalog' },
      { label: 'Aktivity a řemesla', href: '/aktivity' },
      { label: 'Poptávkový formulář', href: '#' },
      { label: 'Časté dotazy', href: '#' },
    ],
  },
  {
    title: 'Pro stánkaře',
    links: [
      { label: 'Registrace stánku', href: '/pridat' },
      { label: 'Ceník služeb', href: '#' },
      { label: 'Administrace', href: '#' },
    ],
  },
];

const Footer = ({ variant = 'service' }: FooterProps) => {
  const columns = variant === 'service' ? serviceColumns : vendorColumns;
  const description = variant === 'service'
    ? 'Největší český portál pro organizátory festivalů a poskytovatele profesionálních služeb.'
    : 'Největší český portál pro organizátory festivalů a majitele prodejních stánků.';
  const newsletterTitle = variant === 'service' ? 'Newsletter' : 'Novinky';
  const buttonText = variant === 'service' ? 'Odebírat novinky' : 'Odebírat';

  return (
    <footer className="bg-white pt-20 pb-12 border-t border-slate-100">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-20">
          {/* Logo & Description */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-8">
              <svg className="w-8 h-8" viewBox="0 0 100 100">
                <circle cx="50" cy="25" fill="#adff2f" r="15" />
                <circle cx="30" cy="65" fill="#e6fbff" r="15" />
                <circle cx="70" cy="65" fill="#fff0f6" r="15" />
              </svg>
              <span className="text-lg font-extrabold text-brand-dark tracking-tight uppercase">
                PROFESTIVAL.CZ
              </span>
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              {description}
            </p>
            <div className="flex gap-3">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-primary transition-all"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <span
                role="img"
                aria-label="Instagram (připravujeme)"
                title="Instagram — připravujeme"
                className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500 cursor-not-allowed"
              >
                <Instagram className="w-5 h-5" />
              </span>
            </div>
          </div>

          {/* Dynamic Columns - side by side on mobile */}
          <div className="grid grid-cols-2 lg:contents gap-8">
            {columns.map((column) => (
              <div key={column.title}>
                <h4 className="font-bold mb-8 text-brand-dark uppercase tracking-widest text-xs">
                  {column.title}
                </h4>
                <ul className="space-y-4 text-sm font-medium">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {link.href === '/pridat' ? (
                        <a
                          href={link.href}
                          className="text-slate-500 hover:text-brand-dark transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-slate-500 hover:text-brand-dark transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold mb-8 text-brand-dark uppercase tracking-widest text-xs">
              {newsletterTitle}
            </h4>
            <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
              <input
                className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-primary/30 text-slate-800 placeholder-slate-400 transition-all text-sm"
                placeholder="Váš e-mail"
                type="email"
              />
              <button
                type="submit"
                className="w-full bg-brand-dark text-white py-3.5 rounded-2xl font-bold uppercase tracking-wider text-xs hover:bg-slate-800 transition-all"
              >
                {buttonText}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-100 pt-10 flex flex-col md:flex-row items-center justify-between gap-6 text-[10px] text-slate-600 font-bold uppercase tracking-[0.2em] text-center md:text-left">
          <p>© 2026 PROFESTIVAL.CZ. Všechna práva vyhrazena.</p>
          <div className="flex gap-8">
            <a href="#" className="hover:text-brand-dark transition-colors">
              Ochrana údajů
            </a>
            <a href="#" className="hover:text-brand-dark transition-colors">
              Podmínky
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
